import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { LOSS_AND_DAMAGE_FILTER_TYPES } from '@/views/screens/Claims/constants';
import { call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_LOSS_AND_DAMAGE_CLAIMS_TABLE_VIEW_COLUMNS } from '../../../contants/ClaimsEnums';
import { getErrorMessageFromAPI, trimObjectValues } from '../../../utilz';
import { LADCovertDatesToMomentType } from '../../../utilz/PreClaimsUtils';
import { formateWithMoment } from '../../../utilz/dates';
import { setAppLoader } from '../../common/CommonActions';
import {
  checkLDClaimXPOExportInError,
  checkLDClaimXPOExportInSuccess,
  createLDClaimInError,
  createLDClaimInSuccess,
  getLADClaimEmailStatusInError,
  getLADClaimEmailStatusInSuccess,
  getLADClaimFilesInError,
  getLADClaimFilesInSuccess,
  getLADClaimItemsInError,
  getLADClaimItemsInSuccess,
  getLADClaimNotesInError,
  getLADClaimNotesInSuccess,
  getLADClaimSettlementsInError,
  getLADClaimSettlementsInSuccess,
  getLDClaimsSortingFieldsInError,
  getLDClaimsSortingFieldsInSuccess,
  getLDClaimsTableViewColumnsInError,
  getLDClaimsTableViewColumnsInSuccess,
  getLossAndDamageClaimDetailsInProgress,
  getLossAndDamageListInError,
  getLossAndDamageListInProgress,
  getLossAndDamageListInSuccess,
  refreshLossAndDamageClaimsList,
  saveLDClaimsTableViewColumnsInError,
  saveLDClaimsTableViewColumnsInSuccess,
  setLossAndDamageClaimsList,
  setLossAndDamageFilters,
  setSelectedLADClaims,
} from './LossAndDamageClaimsActions';
import { getLossAndDamageClaimDetailsInSuccess } from './LossAndDamageClaimsActions';
import { getLossAndDamageClaimDetailsInError } from './LossAndDamageClaimsActions';
import {
  checkDuplicateLDClaimEffect,
  checkLDClaimXpoExportEffect,
  deleteLADClaimsEffect,
  getLADClaimEmailStatusEffect,
  getLADClaimItemsEffect,
  getLADClaimNotesEffect,
  getLADClaimSettlementsEffect,
  getLADSortingFieldsListEffect,
  getLDClaimFilesEffect,
  getLDClaimTableViewColumn,
  getLossAndDamageClaimsListAdvancedEffect,
  getLossAndDamageClaimsListBasicEffect,
  saveLADClaimEffect,
  saveLDClaimTableViewColumn,
  sendLDClaimEmailToCarrierEffect,
  sendLDClaimEmailToXpoCarrierEffect,
} from './LossAndDamageClaimsEffects';
import { getLADClaimDetailsEffect } from './LossAndDamageClaimsEffects';
import LossAndDamageClaimsTypes from './LossAndDamageClaimsTypes';

function* getClaimsListWatcher() {
  const { filters, filterType } = yield select((state) => state.lossAndDamage);
  try {
    const claimStatus = filters.claimStatus.join(',');
    let data;
    if (filterType === LOSS_AND_DAMAGE_FILTER_TYPES.BASIC) {
      const { data: apiData } = yield call(getLossAndDamageClaimsListBasicEffect, { ...filters, claimStatus });
      data = apiData;
    } else {
      const startDate = filters.startDate ? formateWithMoment(filters.startDate, 'YYYY-MM-DD') : undefined;
      const endDate = filters.endDate ? formateWithMoment(filters.endDate, 'YYYY-MM-DD') : undefined;

      const { data: apiData } = yield call(getLossAndDamageClaimsListAdvancedEffect, {
        ...filters,
        startDate,
        endDate,
        claimStatus,
      });
      data = apiData;
    }
    yield put(
      getLossAndDamageListInSuccess({
        data: data.claimsData,
        footerData: data.footerData,
      }),
    );
  } catch (error) {
    const message = getErrorMessageFromAPI(error, 'Failed to get initial data');
    yield put(getLossAndDamageListInError({ message }));
  }
}

function* deleteClaimsWatcher({ payload }) {
  try {
    yield put(setAppLoader(true));
    yield call(deleteLADClaimsEffect, payload);

    yield put(setSelectedLADClaims([]));
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Selected claims are deleted successfully',
    });
    yield put(
      setLossAndDamageFilters({
        pageNo: 1,
      }),
    );
    yield put(getLossAndDamageListInProgress());
    yield put(setAppLoader(false));
  } catch {
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to delete claims',
    });
    yield put(setAppLoader(false));
  }
}

function* getClaimDetailsWatcher({ payload }) {
  try {
    const { data } = yield call(getLADClaimDetailsEffect, payload);
    yield put(
      getLossAndDamageClaimDetailsInSuccess({
        data: LADCovertDatesToMomentType(trimObjectValues(data)),
      }),
    );
  } catch (e) {
    yield put(
      getLossAndDamageClaimDetailsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* updateClaimDetailsWatcher({
  payload: { handleDone, section, pageIndex, isRefetchDetails = true, ...payload },
}) {
  try {
    yield put(setAppLoader(true));
    const { data } = yield call(saveLADClaimEffect, payload);

    if (isRefetchDetails) {
      switch (section) {
        case 'COMMENTS': {
          const { data } = yield call(getLADClaimNotesEffect, {
            claimKey: payload.claimKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getLADClaimNotesInSuccess({ ...data, removeOld: true }));
          break;
        }

        case 'SETTLEMENTS': {
          const { data } = yield call(getLADClaimSettlementsEffect, {
            claimKey: payload.claimKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getLADClaimSettlementsInSuccess({ ...data, removeOld: true }));
          break;
        }

        case 'ITEMS': {
          const { data } = yield call(getLADClaimItemsEffect, {
            claimKey: payload.claimKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getLADClaimItemsInSuccess({ ...data, removeOld: true }));
          break;
        }

        case 'FILES': {
          const { data } = yield call(getLDClaimFilesEffect, {
            claimKey: payload.claimKey,
            pageIndex: 1,
            pageSize: pageIndex * 10,
          });
          yield put(getLADClaimFilesInSuccess({ ...data, removeOld: true }));
          break;
        }

        default: {
          const { data } = yield call(getLADClaimDetailsEffect, payload.claimKey);
          yield put(
            getLossAndDamageClaimDetailsInSuccess({
              data: LADCovertDatesToMomentType(trimObjectValues(data)),
            }),
          );
          break;
        }
      }
      yield put(refreshLossAndDamageClaimsList());
    }
    if (handleDone) {
      handleDone(data);
    }
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully updated claim data.',
    });
  } catch {
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to update claim data.',
    });
  }
}

function* createClaimWatcher({ payload: { handleDone, isRefetch = true, ...payload } }) {
  try {
    yield put(setAppLoader(true));
    const { data } = yield call(saveLADClaimEffect, payload);
    yield put(
      createLDClaimInSuccess({
        data: data,
      }),
    );
    yield put(getLossAndDamageClaimDetailsInProgress(data));
    if (handleDone) {
      handleDone(data);
    }
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully created claim.',
    });
  } catch (e) {
    const message = getErrorMessageFromAPI(e, 'Failed to create claim.');
    yield put(
      createLDClaimInError({
        message,
      }),
    );
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: message,
    });
  }
}

function* sendEmailToCarrierWatcher({ payload: { handleDone, isXpoExport, ...payload } }) {
  try {
    yield put(setAppLoader(true));
    let data = {};
    if (isXpoExport) {
      const res = yield call(sendLDClaimEmailToXpoCarrierEffect, payload);
      data = res.data;
    } else {
      const res = yield call(sendLDClaimEmailToCarrierEffect, payload);
      data = res.data;
    }

    if (handleDone) {
      handleDone(data);
    }
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully sent claim email.',
    });
    yield put(setAppLoader(false));
  } catch {
    yield put(setAppLoader(false));
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to send claim email.',
    });
  }
}

function* checkXpoExportWatcher({ payload: { handleDone, claimKey } }) {
  try {
    const { data } = yield call(checkLDClaimXpoExportEffect, claimKey);
    yield put(
      checkLDClaimXPOExportInSuccess({
        data,
      }),
    );
    if (handleDone) {
      handleDone();
    }
  } catch (e) {
    const message = getErrorMessageFromAPI(e, 'Failed to check the XPO carrier!');
    yield put(
      checkLDClaimXPOExportInError({
        message,
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: message,
    });
  }
}

function* getTableViewColumnsWatcher() {
  try {
    const { data } = yield call(getLDClaimTableViewColumn);
    let columns = DEFAULT_LOSS_AND_DAMAGE_CLAIMS_TABLE_VIEW_COLUMNS;

    if (data?.length > 0) {
      columns = data.map(({ fieldReferenceName, fieldReferenceValue }) => ({
        columnName: fieldReferenceName,
        columnValue: fieldReferenceValue,
      }));
    }
    columns = columns.map((row) => ({ ...row, id: uuidv4() }));
    yield put(
      getLDClaimsTableViewColumnsInSuccess({
        data: columns,
      }),
    );
  } catch (e) {
    yield put(
      getLDClaimsTableViewColumnsInError({
        message: getErrorMessageFromAPI(e, 'Failed to get Loss and Damage claims sorting fields'),
      }),
    );
  }
}

function* getSortingFieldsWatcher() {
  try {
    const { data } = yield call(getLADSortingFieldsListEffect);
    yield put(
      getLDClaimsSortingFieldsInSuccess({
        data,
      }),
    );
  } catch (e) {
    yield put(
      getLDClaimsSortingFieldsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* saveTableViewColumns({ payload: { handleDone, ...payload } }) {
  try {
    yield call(saveLDClaimTableViewColumn, {
      gridName: 'LossAndDamageClaims',
      columnOptions: payload.data.map(({ columnName, columnValue }) => ({
        fieldReferenceName: columnName,
        fieldReferenceValue: columnValue,
      })),
    });
    yield put(
      getLDClaimsTableViewColumnsInSuccess({
        data: payload.data,
      }),
    );
    yield put(
      saveLDClaimsTableViewColumnsInSuccess({
        data: payload.data,
      }),
    );
    if (handleDone) {
      handleDone();
    }
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.SUCCESS,
      message: 'Successfully saved  Loss and Damage claims columns Options',
    });
  } catch (e) {
    yield put(
      saveLDClaimsTableViewColumnsInError({
        message: getErrorMessageFromAPI(e, 'Failed to saved  Loss and Damage claims columns Options'),
      }),
    );
    yield call(showNotification, {
      type: NOTIFICATION_TYPES.ERROR,
      message: 'Failed to saved  Loss and Damage claims columns Options',
    });
  }
}

function* checkDuplicateClaimWatcher({ payload: { handleDone, isInstantValidation, ...payload } }) {
  try {
    if (!isInstantValidation) {
      yield put(setAppLoader(true));
    } else {
      yield delay(1000); // To prevent often calls when while the invoice and pro no fields is blurred
    }

    const { data } = yield call(checkDuplicateLDClaimEffect, payload);

    if (handleDone) {
      handleDone(data);
    }
    if (!isInstantValidation) yield put(setAppLoader(false));
  } catch {
    if (!isInstantValidation) yield put(setAppLoader(false));
  }
}

function* getClaimNotesWatcher({ payload }) {
  try {
    const { data } = yield call(getLADClaimNotesEffect, payload);
    yield put(getLADClaimNotesInSuccess(data));
  } catch (e) {
    yield put(
      getLADClaimNotesInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* getClaimSettlementsWatcher({ payload }) {
  try {
    const { data } = yield call(getLADClaimSettlementsEffect, payload);
    yield put(getLADClaimSettlementsInSuccess(data));
  } catch (e) {
    yield put(
      getLADClaimSettlementsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* getClaimItemsWatcher({ payload }) {
  try {
    const { data } = yield call(getLADClaimItemsEffect, payload);
    yield put(getLADClaimItemsInSuccess(data));
  } catch (e) {
    yield put(
      getLADClaimItemsInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* getClaimEmailStatus({ payload }) {
  try {
    const { data } = yield call(getLADClaimEmailStatusEffect, payload);
    yield put(getLADClaimEmailStatusInSuccess({ data: data.claimEmails }));
  } catch (e) {
    yield put(
      getLADClaimItemsInError({
        message: getLADClaimEmailStatusInError(e),
      }),
    );
  }
}

function* getClaimFilesWatcher({ payload }) {
  try {
    const { data } = yield call(getLDClaimFilesEffect, payload);
    yield put(getLADClaimFilesInSuccess(data));
  } catch (e) {
    yield put(
      getLADClaimFilesInError({
        message: getErrorMessageFromAPI(e),
      }),
    );
  }
}

function* refreshClaimsListWatcher() {
  let { filters, filterType } = yield select((state) => state.lossAndDamage);
  try {
    const claimStatus = filters.claimStatus.join(',');
    let data;
    filters = { ...filters, pageNo: 1, pageSize: filters.pageNo * filters.pageSize };
    if (filterType === LOSS_AND_DAMAGE_FILTER_TYPES.BASIC) {
      const { data: apiData } = yield call(getLossAndDamageClaimsListBasicEffect, { ...filters, claimStatus });
      data = apiData;
    } else {
      const startDate = filters.startDate ? formateWithMoment(filters.startDate, 'YYYY-MM-DD') : undefined;
      const endDate = filters.endDate ? formateWithMoment(filters.endDate, 'YYYY-MM-DD') : undefined;
      const { data: apiData } = yield call(getLossAndDamageClaimsListAdvancedEffect, {
        ...filters,
        startDate,
        endDate,
        claimStatus,
      });
      data = apiData;
    }
    yield put(
      setLossAndDamageClaimsList({
        data: data.claimsData,
        footerData: data.footerData,
      }),
    );
  } catch (error) {
    console.log(error);
  }
}

export default function* LossAndDamageClaims() {
  yield takeEvery(LossAndDamageClaimsTypes.GET_CLAIMS_LIST_PROGRESS, getClaimsListWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.DELETE_CLAIMS_PROGRESS, deleteClaimsWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.GET_CLAIM_DETAILS_PROGRESS, getClaimDetailsWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.UPDATE_CLAIM_DETAILS_PROGRESS, updateClaimDetailsWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.CREATE_CLAIM_PROGRESS, createClaimWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.SEND_EMAIL_TO_CARRIER_PROGRESS, sendEmailToCarrierWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.CHECK_EXP_EXPORT_PROGRESS, checkXpoExportWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.GET_TABLE_VIEW_COLUMNS_PROGRESS, getTableViewColumnsWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.GET_SORTING_FIELDS_PROGRESS, getSortingFieldsWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.SAVE_TABLE_VIEW_COLUMNS_PROGRESS, saveTableViewColumns);
  yield takeLatest(LossAndDamageClaimsTypes.VALIDATE_DUPLICATE_CLAIM_PROGRESS, checkDuplicateClaimWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.GET_CLAIM_NOTES_PROGRESS, getClaimNotesWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.GET_CLAIM_SETTLEMENTS_PROGRESS, getClaimSettlementsWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.GET_CLAIM_ITEMS_PROGRESS, getClaimItemsWatcher);
  yield takeLatest(LossAndDamageClaimsTypes.GET_CLAIM_EMAIL_STATUS_PROGRESS, getClaimEmailStatus);
  yield takeEvery(LossAndDamageClaimsTypes.GET_CLAIM_FILES_PROGRESS, getClaimFilesWatcher);
  yield takeEvery(LossAndDamageClaimsTypes.REFRESH_CLAIMS_LIST, refreshClaimsListWatcher);
}
